import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import QRCode from 'react-qr-code';
import { Link2, QrCode, Download, Printer, FlaskConical } from 'lucide-react';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { Select } from '@/shared/ui/Select';
import patterns from '@/shared/ui/styles/patterns.module.css';
import { useLocation } from '@/shared/hooks/useLocation';
import { reviewKeys } from '../queryKeys';
import { captureAdminApi } from '../api/captureApi';
import styles from './CaptureTab.module.css';

// HTML-entity escape for values interpolated into the print window's raw
// markup (practice name/city come from admin-entered data, not patient input,
// but the print window is built via document.write with no framework escaping).
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Where the code will be placed. Each becomes the link's ?src= tag so scans are
// attributed per placement. The untagged code is the one already printed at the
// front desk, so it keeps its exact URL.
const PLACEMENTS = [
  { value: '', label: 'Front desk & checkout' },
  { value: 'business_card', label: 'Business card' },
  { value: 'receipt', label: 'Receipt' },
  { value: 'flyer', label: 'Flyer' },
];

export function ManualCaptureCard() {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  const qrRef = useRef<HTMLDivElement>(null);
  const testQrRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState('');

  const { data: branding } = useQuery({
    queryKey: reviewKeys.captureBranding(locationId),
    queryFn: () => captureAdminApi.getBranding(locationId),
    enabled: !!locationId,
    staleTime: 10 * 60 * 1000,
  });

  // The link carries the practice's own name (…/review/start/el-cerro-dental).
  // The slug is minted server-side, so the QR/link only renders once branding
  // has loaded — showing an id-based link first would let someone print or copy
  // a URL that isn't the one we hand out.
  const baseUrl = branding?.reviewSlug
    ? `${window.location.origin}/review/start/${branding.reviewSlug}`
    : null;
  const qrUrl = baseUrl && placement ? `${baseUrl}?src=${placement}` : baseUrl;
  // Staff-test variant of the same link: sessions minted through it are flagged
  // isTest and excluded from the funnel and the practice's stats.
  const testQrUrl = baseUrl ? `${baseUrl}?test=1` : null;

  function handleCopyLink() {
    if (qrUrl) {
      navigator.clipboard
        .writeText(qrUrl)
        .catch((err: unknown) => console.warn('Clipboard write failed', err));
    }
  }

  function handleDownloadQr() {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;
    const blob = new Blob([svg.outerHTML], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = placement ? `review-qr-${placement}.svg` : 'review-qr.svg';
    a.click();
    URL.revokeObjectURL(url);
  }

  function handlePrintPage() {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg || !qrUrl) return;

    const practiceName = branding?.practiceName ?? currentLocation?.name ?? 'Our Practice';
    const locationCity = branding?.locationCity ?? null;
    const logoUrl = branding?.logoUrl ?? null;
    const logoOnDark = branding?.logoOnDark ?? false;
    const googleQrUrl = branding?.googleReviewQrUrl ?? null;
    const svgContent = new XMLSerializer().serializeToString(svg);

    const safeName = escapeHtml(practiceName);
    const initials = practiceName
      .split(' ')
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? '')
      .join('');
    const logoBoxHtml = logoUrl
      ? `<img class="logo-box ${logoOnDark ? 'logo-dark' : 'logo-light'}" src="${escapeHtml(logoUrl)}" alt="${safeName}" />`
      : `<div class="logo-box logo-initials">${escapeHtml(initials)}</div>`;
    const headerHtml = `
      <div class="header-row">
        ${logoBoxHtml}
        <div class="header-text">
          <div class="practice-name">${safeName}</div>
          ${locationCity ? `<div class="practice-city">${escapeHtml(locationCity)}</div>` : ''}
        </div>
      </div>`;

    const googleQrSectionHtml = googleQrUrl
      ? `
      <div class="qr-divider"></div>
      <div class="qr-section">
        <div class="qr-label">Google QR Code</div>
        <p class="qr-subtitle">I prefer to post to Google directly instead.</p>
        <div class="qr-wrap"><img class="google-qr-img" src="${escapeHtml(googleQrUrl)}" alt="Google review QR code" /></div>
      </div>`
      : '';

    // Print page opens a new window — these hex values are intentional light-theme constants
    // (the new window has no access to CSS variables or data-theme attributes)
    const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Review QR Code – ${safeName}</title>
  <style>
    @page { size: A4; margin: 0; }
    * { box-sizing: border-box; margin: 0; padding: 0; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    /* Spacing scales differ so a single QR code still fills the page
       generously, while two codes stay compact enough to fit one sheet. */
    body.single-qr {
      --header-mb: 44px;
      --h1-size: 44px;
      --h1-mb: 20px;
      --subtitle-size: 16px;
      --subtitle-mb: 80px;
      --qr-subtitle-mb: 20px;
      --qr-size: 220px;
      --qr-pad: 24px;
      --divider-gap: 22px;
    }
    body.two-qr {
      --header-mb: 26px;
      --h1-size: 32px;
      --h1-mb: 8px;
      --subtitle-size: 13px;
      --subtitle-mb: 50px;
      --qr-subtitle-mb: 12px;
      --qr-size: 150px;
      --qr-pad: 12px;
      --divider-gap: 56px;
    }
    body {
      width: 210mm;
      height: 297mm;
      overflow: hidden;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 26mm 24mm 18mm;
      background: #fbf7f0;
      color: #1f1611;
      text-align: center;
    }
    .header-row {
      display: inline-flex;
      align-items: center;
      gap: 14px;
      margin-bottom: var(--header-mb);
      text-align: left;
    }
    .logo-box {
      width: 64px;
      height: 64px;
      border-radius: 10px;
      object-fit: contain;
      flex-shrink: 0;
      padding: 8px;
    }
    .logo-light {
      background: #fff;
      border: 1px solid #ead9c6;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
      mix-blend-mode: multiply;
    }
    .logo-dark {
      background: #1e1e1e;
    }
    .logo-initials {
      background: #ea580c;
      border: none;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      font-weight: 700;
      color: #fff;
      letter-spacing: 0.02em;
    }
    .practice-name {
      font-size: 18px;
      font-weight: 700;
      color: #1f1611;
      line-height: 1.2;
    }
    .practice-city {
      margin-top: 2px;
      font-size: 13px;
      color: #8a7563;
    }
    h1 {
      font-size: var(--h1-size);
      font-weight: 800;
      line-height: 1.15;
      color: #1f1611;
      margin-bottom: var(--h1-mb);
    }
    .subtitle {
      font-size: var(--subtitle-size);
      color: #8a7563;
      line-height: 1.5;
      max-width: 340px;
      margin-bottom: var(--subtitle-mb);
    }
    .qr-section {
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    .qr-label {
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: #8a7563;
      margin-bottom: 6px;
    }
    .qr-subtitle {
      font-size: 13px;
      color: #6b5645;
      line-height: 1.4;
      max-width: 280px;
      margin-bottom: var(--qr-subtitle-mb);
    }
    .qr-wrap {
      padding: var(--qr-pad);
      border: 2px solid #ead9c6;
      border-radius: 18px;
      background: #fff;
    }
    .qr-wrap svg { display: block; width: var(--qr-size); height: var(--qr-size); }
    .qr-wrap img.google-qr-img { display: block; width: var(--qr-size); height: var(--qr-size); object-fit: contain; }
    .qr-divider {
      width: 220px;
      height: 1px;
      background: #ead9c6;
      margin: var(--divider-gap) 0;
    }
    .footer {
      font-size: 12px;
      color: #d4b896;
      margin-top: auto;
      padding-top: 20px;
    }
  </style>
</head>
<body class="${googleQrUrl ? 'two-qr' : 'single-qr'}">
  ${headerHtml}
  <h1>How was your<br>visit today?</h1>
  <p class="subtitle">Scan the code below to share your experience. It only takes 30 seconds and helps others find us.</p>
  <div class="qr-section">
    <div class="qr-label">PN QR Code</div>
    <p class="qr-subtitle">An AI enabled flow to capture your feedback and submit to Google.</p>
    <div class="qr-wrap">${svgContent}</div>
  </div>
  ${googleQrSectionHtml}
  <div class="footer">Thank you for choosing ${safeName}</div>
  <script>window.addEventListener('load', function() { window.print(); });</script>
</body>
</html>`;

    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
  }

  // Staff-test sheet — deliberately nothing like the patient page (warning
  // palette, dashed border, all-caps banner) so the two printouts can never be
  // mixed up at the front desk. Scans through this code mint flagged sessions
  // that stay out of the funnel and the practice's stats.
  function handlePrintTestQr() {
    const svg = testQrRef.current?.querySelector('svg');
    if (!svg || !testQrUrl) return;

    const practiceName = branding?.practiceName ?? currentLocation?.name ?? 'Our Practice';
    const safeName = escapeHtml(practiceName);
    const svgContent = new XMLSerializer().serializeToString(svg);

    // Same rationale as the patient sheet: the print window has no access to
    // CSS variables, so light-theme hex constants are intentional here.
    const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Staff Test QR – ${safeName}</title>
  <style>
    @page { size: A4; margin: 0; }
    * { box-sizing: border-box; margin: 0; padding: 0; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    body {
      width: 210mm;
      height: 297mm;
      overflow: hidden;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 26mm 24mm 18mm;
      background: #fff;
      color: #1f1611;
      text-align: center;
    }
    .banner {
      font-size: 15px;
      font-weight: 800;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: #92400e;
      background: #fef3c7;
      border: 2px solid #d97706;
      border-radius: 10px;
      padding: 10px 26px;
      margin-bottom: 34px;
    }
    h1 { font-size: 36px; font-weight: 800; line-height: 1.15; margin-bottom: 14px; }
    .subtitle {
      font-size: 15px;
      color: #6b5645;
      line-height: 1.55;
      max-width: 420px;
      margin-bottom: 44px;
    }
    .qr-wrap {
      padding: 24px;
      border: 3px dashed #d97706;
      border-radius: 18px;
      background: #fffbeb;
    }
    .qr-wrap svg { display: block; width: 200px; height: 200px; }
    .steps {
      margin-top: 40px;
      font-size: 13px;
      color: #6b5645;
      line-height: 1.7;
      max-width: 420px;
      text-align: left;
    }
    .footer { font-size: 12px; color: #b45309; margin-top: auto; padding-top: 20px; font-weight: 600; }
  </style>
</head>
<body>
  <div class="banner">Staff test — not for patients</div>
  <h1>Try the review flow</h1>
  <p class="subtitle">Scan this code to walk the patient review experience yourself. Sessions started here are marked as tests and never count toward ${safeName}'s review stats or funnel.</p>
  <div class="qr-wrap">${svgContent}</div>
  <div class="steps">
    Keep this sheet in the back office, away from checkout. The patient-facing QR is the one on the branded "How was your visit today?" page — if a patient scans THIS code by mistake, their review won't be counted.
  </div>
  <div class="footer">Internal use only · ${safeName}</div>
  <script>window.addEventListener('load', function() { window.print(); });</script>
</body>
</html>`;

    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
  }

  return (
    <Card padding="lg">
      <div className={patterns.panelTitle}>
        <QrCode size={16} aria-hidden /> Your review QR code
      </div>

      <p className={styles.captureDesc}>
        Show this on your checkout screen, print it on receipts, or share the link in a follow-up
        text. Each scan creates a fresh invite — no setup needed.
      </p>

      <div className={styles.captureRow}>
        <div ref={qrRef} className={styles.qrWrap}>
          {qrUrl ? (
            <QRCode value={qrUrl} size={104} />
          ) : (
            <div className={styles.qrPlaceholder}>
              <QrCode size={32} aria-hidden />
            </div>
          )}
        </div>

        <div className={styles.captureActions}>
          <Select
            id="review-qr-placement"
            label="Where will this code go?"
            options={PLACEMENTS}
            value={placement}
            onChange={(e) => setPlacement(e.target.value)}
          />
          {qrUrl && (
            <div className={styles.shortLink}>
              <Link2 size={12} aria-hidden />
              <span>{qrUrl}</span>
            </div>
          )}
          <div className={styles.captureBtns}>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Link2 size={14} aria-hidden />}
              onClick={handleCopyLink}
              disabled={!qrUrl}
            >
              Copy link
            </Button>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Download size={14} aria-hidden />}
              onClick={handleDownloadQr}
              disabled={!qrUrl}
            >
              Download QR
            </Button>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Printer size={14} aria-hidden />}
              onClick={handlePrintPage}
              disabled={!qrUrl}
            >
              Print page
            </Button>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<FlaskConical size={14} aria-hidden />}
              onClick={() => testQrUrl && window.open(testQrUrl, '_blank', 'noopener')}
              disabled={!testQrUrl}
              title="Opens the patient flow as a test session — excluded from stats and the funnel"
            >
              Test the flow
            </Button>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Printer size={14} aria-hidden />}
              onClick={handlePrintTestQr}
              disabled={!testQrUrl}
              title="Prints a clearly-marked staff QR for the back office — scans through it are excluded from stats and the funnel"
            >
              Print staff test QR
            </Button>
          </div>
        </div>
      </div>

      {/* Hidden render of the staff-test QR, serialized into the print sheet. */}
      <div ref={testQrRef} className={styles.qrHidden} aria-hidden>
        {testQrUrl && <QRCode value={testQrUrl} size={104} />}
      </div>
    </Card>
  );
}
